import { useState, useEffect, use, useRef } from "react"
import {
    Box,
    Stack,
    Typography,
    Button,
    Snackbar,
    AppBar,
    Toolbar,
    IconButton,
    CircularProgress,
    Tooltip,
    Paper,
} from "@mui/material"
import {
    ArrowBack as ArrowBackIcon,
    MeetingRoom as CabinIcon,
    EventBusy as BookedIcon,
} from "@mui/icons-material"
import WeekHeader from "../components/CabinBooking/WeekHeader";
import DaysHeader from "../components/CabinBooking/DaysHeader";
import CalendarGrid from "../components/CabinBooking/CalendarGrid";
import Legend from "../components/CabinBooking/Legend";
import CabinSelectionDialog from "../components/CabinBooking/CabinSelectionDialog";
import { useTheme } from "@mui/material/styles";
import { format, addDays, isSameDay, addMinutes } from "date-fns"
import { useNavigate } from "react-router-dom"
import { Booking, Cabin, CreateBooking, CreateBulkBooking, GetAvailability, GetCabins, TimeSlotBooking } from "../utils/CabinBookingAPIRequest"
import { User } from "./MainScreen"
import { STORAGE_KEYS } from "../utils/StorageKeys"

// Types
interface TimeSlot {
    time: string
    display: string
}

interface SelectedSlot {
    date: Date
    time: string
}

// Generate 30-minute slots from 9 AM to 6 PM
const generateTimeSlots = (): TimeSlot[] => {
    const slots: TimeSlot[] = []
    const startTime = new Date()
    startTime.setHours(9, 0, 0, 0)

    for (let i = 0; i < 18; i++) {
        // 9 hours * 2 slots per hour = 18 slots
        const time = addMinutes(startTime, i * 30)
        slots.push({
            time: format(time, "HH:mm"),
            display: format(time, "h:mm a"),
        })
    }
    return slots
}

const timeSlots = generateTimeSlots()

export default function CabinBookingPage() {
    const navigate = useNavigate()
    const theme = useTheme();
    const [cabins, setCabins] = useState<Cabin[]>([])
    const [bookings, setBookings] = useState<Booking[]>([])
    const [loading, setLoading] = useState(true)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>([])
    const [dialogSelectedSlots, setDialogSelectedSlots] = useState<SelectedSlot[]>([])
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" })
    const [currentEmployee, setCurrentEmployee] = useState<User | null>(null);
    // Cabin selection and color state
    const [selectedCabinId, setSelectedCabinId] = useState<string | null>(null);
    const cabinColorsRef = useRef<{ [cabinId: string]: string }>({});
    // const [currentUserName] = useState("John Doe") // This should come from your auth context

    useEffect(() => {
        const fetchCurrentEmployee = async () => {
            const user = localStorage.getItem(STORAGE_KEYS.USER);
            if (user) {
                const parsedUser: User = JSON.parse(user);
                setCurrentEmployee(parsedUser);
            }
        }

        fetchCurrentEmployee()

    }, [])

    // Get next 5 working days starting from today (skip weekends)
    const getWeekDates = () => {
        const dates: Date[] = []
        let currentDate = new Date() // Start from today

        while (dates.length < 5) {
            const dayOfWeek = currentDate.getDay() // 0 = Sunday, 6 = Saturday

            // Only add if it's a working day (Monday=1 to Friday=5)
            if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                dates.push(new Date(currentDate))
            }

            // Move to next day
            currentDate = addDays(currentDate, 1)
        }

        return dates
    }

    const weekDates = getWeekDates()


    // Load initial data
    useEffect(() => {
        loadInitialData()
    }, [])

    const loadInitialData = async () => {
        try {
            setLoading(true)

            // Load cabins
            const cabinsResponse = await GetCabins()
            console.log("Cabins response:", cabinsResponse)
            if (cabinsResponse?.status === "success") {
                setCabins(cabinsResponse.data.cabins)
                // Set the first cabin as selected by default
                if (cabinsResponse.data.cabins.length > 0) {
                    setSelectedCabinId(cabinsResponse.data.cabins[0].id)
                }
                console.log("Loaded cabins:", cabinsResponse.data.cabins)
            }

            // Load availability for the week
            const startDate = format(weekDates[0], "yyyy-MM-dd")
            const endDate = format(weekDates[4], "yyyy-MM-dd")
            console.log("Fetching availability for date range:", { startDate, endDate })

            const availabilityResponse = await GetAvailability({ startDate, endDate })
            console.log("Availability response:", availabilityResponse);
            if (availabilityResponse?.status === "success") {
                console.log("Raw bookings from API:", availabilityResponse.data.bookings)
                console.log("Number of bookings:", availabilityResponse.data.bookings?.length || 0)

                // Log each booking to see the structure
                availabilityResponse.data.bookings?.forEach((booking, index) => {
                    console.log(`Booking ${index}:`, {
                        date: booking.date,
                        timeSlot: booking.timeSlot,
                        cabinId: booking.cabinId,
                        status: booking.status,
                        bookedBy: booking.bookedBy,
                        fullBooking: booking
                    })
                })

                // Normalize the bookings data if needed
                const normalizedBookings = availabilityResponse.data.bookings.map(booking => ({
                    ...booking,
                    // Normalize time format if it comes as "09:00:00" to "09:00"
                    timeSlot: booking.timeSlot?.substring(0, 5) || booking.timeSlot,
                    // Ensure status is set if missing
                    status: booking.status || "active"
                }))

                console.log("Normalized bookings:", normalizedBookings)
                setBookings(normalizedBookings)
            }
        } catch (error) {
            console.error("Error loading initial data:", error)
            setSnackbar({
                open: true,
                message: "Error loading cabin data. Please refresh the page.",
                severity: "error",
            })
        } finally {
            setLoading(false)
        }
    }

    // Check if cabin is booked for specific slot
    const isCabinBooked = (date: Date, timeSlot: string, cabinId: string) => {
        const dateStr = format(date, "yyyy-MM-dd")

        // Add debugging
        console.log("Checking booking for:", { dateStr, timeSlot, cabinId })
        console.log("Available bookings:", bookings.filter(b => b.status === "active"))

        const isBooked = bookings.some((booking) => {
            const match = booking.date === dateStr &&
                booking.timeSlot === timeSlot &&
                booking.cabinId === cabinId &&
                booking.status === "active"

            // Debug potential matches
            if (booking.date === dateStr && booking.cabinId === cabinId) {
                console.log("Potential match found:", {
                    booking,
                    timeSlot,
                    match,
                    timeSlotMatch: booking.timeSlot === timeSlot,
                    statusMatch: booking.status === "active"
                })
            }

            return match
        })

        console.log("Is booked result:", isBooked)
        return isBooked
    }

    // Get booking for specific cabin and slot
    const getBookingForSlot = (date: Date, timeSlot: string, cabinId: string) => {
        const dateStr = format(date, "yyyy-MM-dd")
        return bookings.find(
            (booking) =>
                booking.date === dateStr &&
                booking.timeSlot === timeSlot &&
                booking.cabinId === cabinId &&
                booking.status === "active",
        )
    }

    // Get available cabins for a time slot
    const getAvailableCabins = (date: Date, timeSlot: string) => {
        return cabins.filter((cabin) => !isCabinBooked(date, timeSlot, cabin.id))
    }

    // For selected cabin, check if booked for slot
    const isSelectedCabinBooked = (date: Date, timeSlot: string) => {
        if (!selectedCabinId) return false;
        return isCabinBooked(date, timeSlot, selectedCabinId);
    };

    // Get available cabins for all dialog selected slots
    const getAvailableCabinsForDialogSlots = () => {
        if (dialogSelectedSlots.length === 0) return []

        // Start with all cabins
        let availableCabins = [...cabins]

        // For each selected slot, filter out cabins that are booked
        dialogSelectedSlots.forEach((slot) => {
            const cabinsAvailableForThisSlot = getAvailableCabins(slot.date, slot.time)
            availableCabins = availableCabins.filter((cabin) =>
                cabinsAvailableForThisSlot.some((availableCabin) => availableCabin.id === cabin.id),
            )
        })

        return availableCabins
    }

    // Get booked cabins for a time slot
    const getBookedCabins = (date: Date, timeSlot: string) => {
        return cabins.filter((cabin) => isCabinBooked(date, timeSlot, cabin.id))
    }

    // Check if a slot is selected in the dialog
    const isDialogSlotSelected = (date: Date, timeSlot: string) => {
        return dialogSelectedSlots.some(
            (slot) => format(slot.date, "yyyy-MM-dd") === format(date, "yyyy-MM-dd") && slot.time === timeSlot,
        )
    }

    // Handle slot click - always open dialog
    const handleSlotClick = (date: Date, timeSlot: string) => {
        // Set the initial slot and open dialog
        setDialogSelectedSlots([{ date, time: timeSlot }])
        setDialogOpen(true)
    }

    // Handle slot selection in dialog
    const handleDialogSlotToggle = (date: Date, timeSlot: string) => {
        const isAlreadySelected = isDialogSlotSelected(date, timeSlot)

        if (isAlreadySelected) {
            // Remove from selection (but keep at least one slot)
            if (dialogSelectedSlots.length > 1) {
                setDialogSelectedSlots(
                    dialogSelectedSlots.filter(
                        (slot) => !(format(slot.date, "yyyy-MM-dd") === format(date, "yyyy-MM-dd") && slot.time === timeSlot),
                    ),
                )
            }
        } else {
            // Add to selection
            setDialogSelectedSlots([...dialogSelectedSlots, { date, time: timeSlot }])
        }
    }

    // Open dialog for bulk booking
    const openBulkBookingDialog = () => {
        if (selectedSlots.length > 0) {
            setDialogOpen(true)
        } else {
            setSnackbar({
                open: true,
                message: "Please select at least one time slot for bulk booking",
                severity: "error",
            })
        }
    }

    // ...removed old handleCabinBook, only new version remains...

    // Get slot status (for selected cabin)
    const getSlotStatus = (date: Date, timeSlot: string) => {
        if (!selectedCabinId) return { booked: 0, total: 1, available: 1 };
        const booked = isCabinBooked(date, timeSlot, selectedCabinId) ? 1 : 0;
        return { booked, total: 1, available: booked ? 0 : 1 };
    };

    // Get slot color based on availability (for selected cabin)
    const getSlotColor = (date: Date, timeSlot: string) => {
        if (!selectedCabinId) return "#f5f5f5";
        return isCabinBooked(date, timeSlot, selectedCabinId) ? "#ffcdd2" : "#e8f5e9";
    };

    // Clear all selected slots
    const clearSelectedSlots = () => {
        setSelectedSlots([])
    }

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
                <CircularProgress size={60} />
            </Box>
        )
    }

    // Cabin panel UI
    const renderCabinPanels = () => (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, overflowX: 'auto', p: 2 }}>
            {cabins.map((cabin) => {
                const isSelected = selectedCabinId === cabin.id;
                return (
                    <Box
                        key={cabin.id}
                        onClick={() => setSelectedCabinId(cabin.id)}
                        sx={{
                            minWidth: 120,
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            cursor: 'pointer',
                            background: cabin.color || theme.palette.primary.light,
                            border: isSelected ? '2px solid #eee' : null,
                            boxShadow: isSelected ? 8 : 0,
                            color: '#fff',
                            fontWeight: 400,
                            textAlign: 'center',
                            transition: 'border 0.2s, box-shadow 0.2s',
                            userSelect: 'none',
                        }}
                    >
                        {cabin.name}
                    </Box>
                );
            })}
        </Box>
    );

    // Custom calendar grid for selected cabin with aligned header and body
    const renderCalendarGrid = () => (
        <Box>
            {/* Time slots as rows */}
            {timeSlots.map((slot) => (
                <Box key={slot.time} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ minWidth: 100, maxWidth: 120, width: 120, textAlign: 'center', color: theme.palette.text.secondary, fontWeight: 500 }}>
                        {slot.display}
                    </Box>
                    {weekDates.map((date) => {
                        const booked = selectedCabinId ? isCabinBooked(date, slot.time, selectedCabinId) : false;
                        let bookedUserName = '';
                        if (booked && selectedCabinId) {
                            const booking = getBookingForSlot(date, slot.time, selectedCabinId);
                            bookedUserName = booking?.bookedBy || '';
                        }
                        return (
                            booked && bookedUserName ? (
                                <Tooltip key={format(date, 'yyyy-MM-dd')} title={`Booked by: ${bookedUserName}`}>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            p: 1,
                                            minHeight: 48,
                                            border: `2px solid #e53935`,
                                            borderRadius: 2,
                                            background: theme.palette.background.paper,
                                            boxShadow: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'default',
                                            opacity: selectedCabinId ? 1 : 0.5,
                                            pointerEvents: selectedCabinId ? 'auto' : 'none',
                                            fontWeight: 500,
                                            fontSize: 22,
                                            transition: 'border 0.2s, background 0.2s, box-shadow 0.2s',
                                            mx: 1,
                                        }}
                                    >
                                        <BookedIcon sx={{ color: '#e53935', fontSize: 28 }} />
                                    </Box>
                                </Tooltip>
                            ) : (
                                <Box
                                    key={format(date, 'yyyy-MM-dd')}
                                    sx={{
                                        flex: 1,
                                        p: 1,
                                        minHeight: 48,
                                        border: `2px solid ${booked ? '#e53935' : theme.palette.background.paper}`,
                                        borderRadius: 2,
                                        background: theme.palette.background.paper,
                                        boxShadow: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: booked ? 'default' : 'pointer',
                                        opacity: selectedCabinId ? 1 : 0.5,
                                        pointerEvents: selectedCabinId ? 'auto' : 'none',
                                        fontWeight: 500,
                                        fontSize: 18,
                                        transition: 'border 0.2s, background 0.2s, box-shadow 0.2s',
                                        mx: 1,
                                    }}
                                    onClick={() => {
                                        if (!booked && selectedCabinId) {
                                            // Book directly
                                            handleCabinBook(selectedCabinId, date, slot.time);
                                        }
                                    }}
                                >
                                    Book now
                                </Box>
                            )
                        );
                    })}
                </Box>
            ))}
        </Box>
    );

    // Overload handleCabinBook for direct booking from grid
    const handleCabinBook = async (cabinId: string, date?: Date, timeSlot?: string) => {
        // If called from dialog, use dialogSelectedSlots; if from grid, use date/timeSlot
        if (date && timeSlot) {
            // Book single slot for selected cabin
            try {
                if (!currentEmployee) {
                    setSnackbar({
                        open: true,
                        message: "No employee selected. Please log in or select an employee.",
                        severity: "error",
                    });
                    return;
                }
                const response = await CreateBooking({
                    username: currentEmployee.id,
                    cabinId,
                    date: format(date, "yyyy-MM-dd"),
                    timeSlot,
                    title: "Meeting",
                });
                if (response?.status === "success") {
                    setSnackbar({
                        open: true,
                        message: "Cabin booked successfully!",
                        severity: "success",
                    });
                    setBookings([...bookings, response.data.booking]);
                } else {
                    throw new Error(response?.data.message || "Booking failed");
                }
            } catch (error: any) {
                setSnackbar({
                    open: true,
                    message: error.message || "Error creating booking. Please try again.",
                    severity: "error",
                });
            }
            return;
        }
        // ...existing code for dialog bulk booking...
        if (dialogSelectedSlots.length === 0) return;
        try {
            if (dialogSelectedSlots.length === 1) {
                const slot = dialogSelectedSlots[0];
                if (!currentEmployee) {
                    setSnackbar({
                        open: true,
                        message: "No employee selected. Please log in or select an employee.",
                        severity: "error",
                    });
                    return;
                }
                const response = await CreateBooking({
                    username: currentEmployee.id,
                    cabinId,
                    date: format(slot.date, "yyyy-MM-dd"),
                    timeSlot: slot.time,
                    title: "Meeting",
                });
                if (response?.status === "success") {
                    setSnackbar({
                        open: true,
                        message: "Cabin booked successfully!",
                        severity: "success",
                    });
                    setBookings([...bookings, response.data.booking]);
                } else {
                    throw new Error(response?.data.message || "Booking failed");
                }
            } else {
                const timeSlots: TimeSlotBooking[] = dialogSelectedSlots.map((slot) => ({
                    date: format(slot.date, "yyyy-MM-dd"),
                    timeSlot: slot.time,
                }));
                const response = await CreateBulkBooking({
                    username: currentEmployee?.id || "",
                    cabinId,
                    timeSlots,
                    title: "Meeting",
                });
                if (response?.status === "success") {
                    setSnackbar({
                        open: true,
                        message: `Successfully booked ${dialogSelectedSlots.length} time slots!`,
                        severity: "success",
                    });
                    setBookings([...bookings, ...response.data.bookings]);
                } else {
                    throw new Error(response?.data.message || "Bulk booking failed");
                }
            }
            setDialogOpen(false);
            setDialogSelectedSlots([]);
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error.message || "Error creating booking. Please try again.",
                severity: "error",
            });
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            {/* Main Content */}
            <Paper elevation={3} sx={{ p: 3 }}>
                <Stack spacing={3}>
                    {/* Week Header */}
                    {/* <WeekHeader weekDates={weekDates} /> */}
                    {/* Cabin Panels */}
                    {renderCabinPanels()}

                    {/* Days Header */}
                    <DaysHeader weekDates={weekDates} today={new Date()} theme={theme} />
                    {/* Calendar Grid for selected cabin */}
                    {selectedCabinId ? renderCalendarGrid() : (
                        <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', mt: 2 }}>
                            Please select a cabin to view and book slots.
                        </Typography>
                    )}
                    {/* Legend */}
                    <Box sx={{ mt: 2, mb: 1 }}>
                        <Paper elevation={3} sx={{ p: 3 }}>
                            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                                Legend
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box component="span" sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 28,
                                        height: 28,
                                        // border: '2px solid #43a047',
                                        borderRadius: 2,
                                        background: theme.palette.background.paper,
                                        boxShadow: 2,
                                        mr: 1
                                    }} />
                                    <Typography variant="body2" color="textSecondary">Available</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box component="span" sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 28,
                                        height: 28,
                                        border: '2px solid #e53935',
                                        borderRadius: 2,
                                        background: theme.palette.background.paper,
                                        boxShadow: 2,
                                        mr: 1
                                    }}>
                                        <BookedIcon sx={{ color: '#e53935', fontSize: 20 }} />
                                    </Box>
                                    <Typography variant="body2" color="textSecondary">Booked</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                </Stack>
            </Paper>

            {/* Cabin Selection Dialog (kept for bulk booking, if needed) */}
            <CabinSelectionDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                dialogSelectedSlots={dialogSelectedSlots}
                setDialogSelectedSlots={setDialogSelectedSlots}
                timeSlots={timeSlots}
                getSlotStatus={getSlotStatus}
                isDialogSlotSelected={isDialogSlotSelected}
                handleDialogSlotToggle={handleDialogSlotToggle}
                getAvailableCabinsForDialogSlots={getAvailableCabinsForDialogSlots}
                handleCabinBook={handleCabinBook}
                getBookedCabins={getBookedCabins}
                getBookingForSlot={getBookingForSlot}
            />

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={
                    <Typography variant="body2" color={snackbar.severity === "error" ? "error.main" : "success.main"}>
                        {snackbar.message}
                    </Typography>
                }
            />
        </Box>
    );
}

